import _ from 'lodash';

import { LoanApplicationTabName } from 'constants/loanApplication';
import { checkIsRecipientCopy, getRecipientCopyLabel } from 'utils/documents';

import { SearchCategory } from '../UniversalSearch.constants';

import { resolveFieldValue } from './fields.utils';
import {
  BORROWER_SECTION_FIELD_DEFINITIONS,
  LOAN_CLOSING_SECTION_FIELD_DEFINITIONS,
} from './sections.config';
import {
  ALERTS_TAB_ROUTE,
  BORROWERS_TAB_NAME,
  BORROWERS_TAB_ROUTE,
  BorrowerSectionName,
  CONDITION_MESSAGE_PREVIEW_LENGTH,
  CONTACTS_TAB_NAME,
  CONTACTS_TAB_ROUTE,
  DOCUMENTS_TAB_ROUTE,
  LOAN_CLOSING_TAB_NAME,
  LOAN_CLOSING_TAB_ROUTE,
  LoanClosingSectionName,
  UNDERWRITING_TAB_ROUTE,
} from './sections.constants';

const buildBorrowerSectionFieldItems = ({ borrower, sectionHref, sectionName }) => {
  const fieldDefinitions = BORROWER_SECTION_FIELD_DEFINITIONS[sectionHref];

  if (!fieldDefinitions?.length) {
    return [];
  }

  return fieldDefinitions.map(({ fieldId, label, dataPath }) => {
    const fieldValue = resolveFieldValue({ dataSource: borrower, dataPath });

    return {
      id: `borrower-section-field-${borrower.borrowerId}-${sectionHref}-${fieldId}`,
      label: `${borrower.fullName} > ${label}`,
      category: SearchCategory.RECORDS,
      breadcrumb: `${BORROWERS_TAB_NAME} > ${sectionName}`,
      tabRoute: BORROWERS_TAB_ROUTE,
      borrowerSection: sectionHref,
      recordId: borrower.borrowerId,
      fieldId,
      isRecordField: true,
      ...(fieldValue ? { fieldValue } : {}),
    };
  });
};

export const buildBorrowerSectionsIndex = ({ borrowers = [] }) =>
  borrowers.flatMap(borrower =>
    Object.entries(BorrowerSectionName).flatMap(([sectionHref, sectionName]) => {
      const sectionItem = {
        id: `borrower-section-${borrower.borrowerId}-${sectionHref}`,
        label: sectionName,
        category: SearchCategory.RECORDS,
        breadcrumb: `${BORROWERS_TAB_NAME} > ${borrower.fullName}`,
        tabRoute: BORROWERS_TAB_ROUTE,
        borrowerSection: sectionHref,
        recordId: borrower.borrowerId,
      };

      const fieldItems = buildBorrowerSectionFieldItems({
        borrower,
        sectionHref,
        sectionName,
      });

      return [sectionItem, ...fieldItems];
    }),
  );

export const buildLoanClosingSectionsIndex = ({ loanClosingData } = {}) =>
  Object.entries(LoanClosingSectionName).flatMap(([sectionHref, sectionName]) => {
    const sectionItem = {
      id: `loan-closing-section-${sectionHref}`,
      label: sectionName,
      category: SearchCategory.NAVIGATION,
      breadcrumb: LOAN_CLOSING_TAB_NAME,
      tabRoute: LOAN_CLOSING_TAB_ROUTE,
      loanClosingSection: sectionHref,
    };

    const fieldDefinitions = LOAN_CLOSING_SECTION_FIELD_DEFINITIONS[sectionHref];

    const fieldItems = fieldDefinitions
      ? fieldDefinitions.map(({ fieldId, label, dataPath }) => {
          const fieldValue = resolveFieldValue({
            dataSource: loanClosingData,
            dataPath: dataPath || fieldId,
          });

          return {
            id: `loan-closing-field-${sectionHref}-${fieldId}`,
            label,
            category: SearchCategory.FIELDS,
            breadcrumb: `${LOAN_CLOSING_TAB_NAME} > ${sectionName}`,
            tabRoute: LOAN_CLOSING_TAB_ROUTE,
            loanClosingSection: sectionHref,
            fieldId,
            ...(fieldValue ? { fieldValue } : {}),
          };
        })
      : [];

    return [sectionItem, ...fieldItems];
  });

export const buildDocumentsIndex = ({
  documents = {},
  documentTypes = {},
  documentCategories = [],
}) => {
  const typeToCategoryName = (documentCategories || []).reduce((map, category) => {
    (category.documentTypeIds || []).forEach(typeId => {
      map.set(typeId, category.documentCategoryName);
    });

    return map;
  }, new Map());

  return Object.values(documents)
    .filter(doc => doc && !doc.isDeleted && doc.documentTypeId)
    .map(doc => {
      const typeName = documentTypes[doc.documentTypeId]?.documentTypeName;
      const categoryName = typeToCategoryName.get(doc.documentTypeId);
      const breadcrumb = categoryName ? `Documents > ${categoryName}` : 'Documents';

      const label = checkIsRecipientCopy(doc)
        ? `${typeName} (${getRecipientCopyLabel(doc.RecipientSignerName)})`
        : typeName;

      return typeName
        ? {
            id: `doc-${doc.id}`,
            label,
            category: SearchCategory.DOCUMENTS,
            breadcrumb,
            tabRoute: DOCUMENTS_TAB_ROUTE,
            documentId: doc.id,
          }
        : null;
    })
    .filter(Boolean);
};

export const buildDocumentCategoriesIndex = ({ documentCategories = [], documents = {} }) => {
  const loanTypeIds = new Set(
    Object.values(documents)
      .filter(doc => doc && !doc.isDeleted && doc.documentTypeId)
      .map(doc => doc.documentTypeId),
  );

  return (documentCategories || [])
    .filter(category => (category.documentTypeIds || []).some(typeId => loanTypeIds.has(typeId)))
    .map(category => ({
      id: `doc-cat-${category.documentCategoryId}`,
      label: category.documentCategoryName,
      category: SearchCategory.DOCUMENTS,
      breadcrumb: 'Documents',
      tabRoute: DOCUMENTS_TAB_ROUTE,
    }));
};

const getConditionLabel = condition => {
  const message = condition.message || condition.internalText || '';

  return message.length > CONDITION_MESSAGE_PREVIEW_LENGTH
    ? `${message.slice(0, CONDITION_MESSAGE_PREVIEW_LENGTH)}...`
    : message;
};

export const buildConditionsIndex = ({ conditionGroups }) => {
  if (_.isEmpty(conditionGroups)) {
    return [];
  }

  return conditionGroups.flatMap(([priorToType, conditions]) =>
    conditions.map(condition => ({
      id: `condition-${condition.id}`,
      label: getConditionLabel(condition),
      category: SearchCategory.CONDITIONS,
      breadcrumb: `Underwriting > Conditions > ${priorToType}`,
      tabRoute: UNDERWRITING_TAB_ROUTE,
      conditionId: condition.id,
      priorToType,
    })),
  );
};

export const buildTasksIndex = ({ taskGroups }) => {
  if (_.isEmpty(taskGroups)) {
    return [];
  }

  return Object.entries(taskGroups).flatMap(([status, tasks]) =>
    tasks.map(task => ({
      id: `task-${task.taskId}`,
      label: task.name,
      category: SearchCategory.CONDITIONS,
      breadcrumb: `Tasks > ${_.startCase(status)}`,
      tabRoute: _.kebabCase(LoanApplicationTabName.TASKS),
      taskId: task.taskId,
      taskStatus: status,
    })),
  );
};

export const buildContactsIndex = ({ contacts }) => {
  if (_.isEmpty(contacts)) {
    return [];
  }

  return contacts.map(contact => {
    const name = [contact.fullName?.firstName, contact.fullName?.lastName]
      .filter(Boolean)
      .join(' ');
    const label = name || contact.companyName || contact.contactTypeName || 'Contact';
    const breadcrumbParts = [CONTACTS_TAB_NAME];

    if (contact.contactTypeName) {
      breadcrumbParts.push(contact.contactTypeName);
    }

    return {
      id: `contact-${contact.id}`,
      label,
      category: SearchCategory.CONTACTS,
      breadcrumb: breadcrumbParts.join(' > '),
      tabRoute: CONTACTS_TAB_ROUTE,
      contactId: contact.id,
      ...(contact.companyName && name ? { fieldValue: contact.companyName } : {}),
      ...(contact.email ? { contactEmail: contact.email } : {}),
    };
  });
};

export const buildChecklistsIndex = ({ checklistResults }) => {
  if (_.isEmpty(checklistResults)) {
    return [];
  }

  return checklistResults
    .filter(result => result?.status?.result || result?.checklist)
    .map(result => {
      const checklistName = result.status?.result?.checklist?.name || result.checklist?.name;
      const checklistId = result.target?.checklistId;

      if (!checklistName || !checklistId) {
        return null;
      }

      return {
        id: `checklist-${checklistId}`,
        label: checklistName,
        category: SearchCategory.NAVIGATION,
        breadcrumb: 'Alerts',
        tabRoute: ALERTS_TAB_ROUTE,
        checklistId,
      };
    })
    .filter(Boolean);
};
